import api from '../../../../services/api';

export const getSuggestionsList = async (params = {}) => {
  const {
    knowledgebase_uuid,
    fact_uuid,
    page = 1,
    page_size = 20,
    order_by = 'created_at',
    order_direction = 'DESC',
    search = {},
  } = params;

  // Validations
  if (!knowledgebase_uuid && !fact_uuid) {
    throw new Error('At least one of knowledgebase_uuid or fact_uuid must be provided');
  }

  if (page < 1) {
    throw new Error('page must be >= 1');
  }

  if (page_size < 1 || page_size > 100) {
    throw new Error('page_size must be between 1 and 100');
  }

  const validOrderBy = ['created_at', 'updated_at'];
  if (!validOrderBy.includes(order_by)) {
    throw new Error(`order_by must be one of: ${validOrderBy.join(', ')}`);
  }

  if (order_direction !== 'ASC' && order_direction !== 'DESC') {
    throw new Error('order_direction must be ASC or DESC');
  }

  try {
    const response = await api.post('/v1/fact/suggestion/list', {
      page,
      page_size,
      order_by,
      order_direction,
      search,
      filters: {
        ...(knowledgebase_uuid ? { knowledgebase_uuid } : {}),
        ...(fact_uuid ? { fact_uuid } : {}),
      },
    });

    return response.data;
  } catch (error) {
    console.warn('[getSuggestionsList] Error calling endpoint, returning mock data:', error.message);
    
    // Mock data structure matching API response
    const mockData = {
      statusCode: 200,
      msgCode: [
        {
          code: 'FACT_SUGGESTIONS_LISTED',
          description: 'Fact suggestions listed successfully.',
        },
      ],
      requestId: `mock-${Date.now()}`,
      body: [
        {
          suggestion_uuid: '11111111-1111-1111-1111-111111111111',
          fact_uuid: '22222222-2222-2222-2222-222222222222',
          current_value: 'Mon-Fri: 9AM-7PM, Sat: 10AM-6PM, Sun: Closed',
          new_value: 'Mon-Fri: 9AM-8PM, Sat: 10AM-6PM, Sun: 11AM-5PM',
          comment: 'Please update this based on the latest policy.',
          created_by: 'John Doe',
          created_by_role: 'MANAGER',
          created_by_photo: 'https://cdn.example.com/u/john.png',
          created_at: '2026-01-01T12:00:00Z',
        },
        {
          suggestion_uuid: '22222222-2222-2222-2222-222222222222',
          fact_uuid: '22222222-2222-2222-2222-222222222222',
          current_value: 'Mon-Fri: 9AM-7PM, Sat: 10AM-6PM, Sun: Closed',
          new_value: 'Mon-Fri: 8:30AM-7PM, Sat: 9AM-6PM, Sun: Closed',
          comment: 'Based on personal observation and recent schedule adjustments.',
          created_by: 'Sarah Martinez',
          created_by_role: 'MANAGER',
          created_by_photo: null,
          created_at: '2026-01-02T10:00:00Z',
        },
        {
          suggestion_uuid: '33333333-3333-3333-3333-333333333333',
          fact_uuid: '33333333-3333-3333-3333-333333333333',
          current_value: 'Standard 1-year contract',
          new_value: 'Standard 2-year contract with renewal option',
          comment: 'Extracted from contract templates and historical manager agreements.',
          created_by: 'AI Assistant',
          created_by_role: 'AI',
          created_by_photo: null,
          created_at: '2026-01-03T14:30:00Z',
        },
        {
          suggestion_uuid: '44444444-4444-4444-4444-444444444444',
          fact_uuid: '44444444-4444-4444-4444-444444444444',
          current_value: '2-year standard warranty',
          new_value: '3-year comprehensive warranty',
          comment: 'High confidence based on warranty documentation and product specifications.',
          created_by: 'AI Assistant',
          created_by_role: 'AI',
          created_by_photo: null,
          created_at: '2026-01-04T09:15:00Z',
        },
      ],
      meta: {
        totalItems: 4,
        totalPages: 1,
        currentPage: page,
        perPage: page_size,
        hasNext: false,
        hasPrevious: false,
        nextCursor: null,
      },
    };

    // Aplicar filtros y búsqueda a los datos mock
    let body = Array.isArray(mockData.body) ? mockData.body : [];
    const searchKeyword = search?.keyword;
    if (searchKeyword && searchKeyword.trim() !== '') {
      const searchLower = searchKeyword.toLowerCase().trim();
      body = body.filter((item) => {
        const comment = (item.comment || '').toLowerCase();
        const currentValue = (item.current_value || '').toLowerCase();
        const newValue = (item.new_value || '').toLowerCase();
        return comment.includes(searchLower) || 
               currentValue.includes(searchLower) || 
               newValue.includes(searchLower);
      });
    }

    // Aplicar ordenamiento
    body.sort((a, b) => {
      let aValue, bValue;
      
      switch (order_by) {
        case 'created_at':
        default:
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
      }
      
      return order_direction === 'ASC' ? aValue - bValue : bValue - aValue;
    });

    // Aplicar paginación
    const totalItems = body.length;
    const startIndex = (page - 1) * page_size;
    const endIndex = startIndex + page_size;
    const paginatedBody = body.slice(startIndex, endIndex);

    return {
      ...mockData,
      body: paginatedBody,
      meta: {
        totalItems,
        totalPages: Math.ceil(totalItems / page_size),
        currentPage: page,
        perPage: page_size,
        hasNext: endIndex < totalItems,
        hasPrevious: page > 1,
        nextCursor: null,
      },
    };
  }
};


export const approveSuggestion = async (suggestion_uuid) => {
  // Validations
  if (!suggestion_uuid) {
    throw new Error('suggestion_uuid is required');
  }


  try {
    const response = await api.post('/v1/fact/suggestion/approve', {
      suggestion_uuid,
    });

    return response.data;
  } catch (error) {
    console.warn('[approveSuggestion] Error calling endpoint:', error.message);
    
    // Mock response for development
    return {
      statusCode: 200,
      msgCode: [
        {
          code: 'FACT_SUGGESTION_APPROVED',
          description: 'Fact suggestion approved successfully.',
        },
      ],
      requestId: `mock-${Date.now()}`,
      body: {
        suggestion_uuid,
        fact_uuid: 'mock-fact-uuid',
      },
      meta: null,
    };
  }
};


export const rejectSuggestion = async (suggestion_uuid) => {
  // Validations
  if (!suggestion_uuid) {
    throw new Error('suggestion_uuid is required');
  }


  try {
    const response = await api.post('/v1/fact/suggestion/reject', {
      suggestion_uuid,
    });

    return response.data;
  } catch (error) {
    console.warn('[rejectSuggestion] Error calling endpoint:', error.message);
    
    // Mock response for development
    return {
      statusCode: 200,
      msgCode: [
        {
          code: 'FACT_SUGGESTION_REJECTED',
          description: 'Fact suggestion rejected successfully.',
        },
      ],
      requestId: `mock-${Date.now()}`,
      body: {
        suggestion_uuid,
        fact_uuid: 'mock-fact-uuid',
      },
      meta: null,
    };
  }
};

export const createSuggestion = async (data) => {
  const { fact_uuid, value_text, comment } = data;
  
  // Validations
  if (!fact_uuid) {
    throw new Error('fact_uuid is required');
  }
  if (!value_text || !value_text.trim()) {
    throw new Error('value_text is required');
  }
  if (!comment || !comment.trim()) {
    throw new Error('comment is required');
  }
  
  try {
    const response = await api.post('/v1/fact/suggestion/create', {
      factUuid: fact_uuid,
      valueText: value_text.trim(),
      comment: comment.trim(),
    });
    
    return response.data;
  } catch (error) {
    console.error('[createSuggestion] Error calling endpoint:', error.message);
    throw error;
  }
};

